import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

/**
 * The labels for a given media query.
 */
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The context for the viewport size, either 'narrow' when the mobile
 * menu is visible, or 'wide' for viewport widths above that point.
 */
export const ViewportContext = /*#__PURE__*/createContext(null);

/**
 * A provider used _only_ to conditionally render components. Typically,
 * for performance reasons, you should use `mediaQueries` within a
 * styled component when a component's CSS properties change
 * across breakpoints but there is no need to conditionally render it or move
 * the HTML/markup.
 *
 * Pass in `queries` for `isMobile` and `isDesktop` to test whether each query
 * matches. Then, use the `useViewport` hook to check those values in descendant
 * components.
 *
 * @example
 * ```tsx
 * <ViewportProvider
 *   queries={{
 *     isMobile: `(max-width: 1023px)`,
 *     isDesktop: `(min-width: 1024px)`,
 *     isBelowLaptop: `(max-width: 1439px)`,
 *     isAboveColumn: `(min-width: 1200px)`,
 *     isBetweenMobileAndColumn: `(min-width: 1024px) and (max-width: 1199px)`,
 *   }}
 * >
 *   children here
 * </ViewportProvider>
 * ```
 *
 * @see https://private.hubteamqa.com/developer-docs-shared-components-storybook/?path=/story/utils-media-queries-and-breakpoints--page
 *
 */
export const ViewportProvider = ({
  children,
  queries
}) => {
  const keys = useMemo(() => Object.keys(queries), [queries]);
  const getQueryMatches = useCallback(() => {
    return keys.reduce((acc, media) => {
      acc[media] = window.matchMedia(queries[media]).matches;
      return acc;
    }, {});
  }, [queries, keys]);
  const [queryMatch, setQueryMatch] = useState(getQueryMatches);
  useEffect(() => {
    const mediaQueryLists = {};
    let isAttached = false;
    const handleQueryListener = () => {
      const updatedMatches = keys.reduce((acc, media) => {
        acc[media] = !!(mediaQueryLists[media] && mediaQueryLists[media].matches);
        return acc;
      }, {});
      setQueryMatch(updatedMatches);
    };
    if (window && window.matchMedia) {
      setQueryMatch(getQueryMatches);
      isAttached = true;
      keys.forEach(media => {
        mediaQueryLists[media] = window.matchMedia(queries[media]);
        window.matchMedia(queries[media]).addEventListener('change', handleQueryListener);
      });
    }
    return () => {
      if (isAttached) {
        keys.forEach(media => {
          mediaQueryLists[media].removeEventListener('change', handleQueryListener);
        });
      }
    };
  }, [getQueryMatches, keys, queries]);
  return /*#__PURE__*/_jsx(ViewportContext.Provider, {
    value: queryMatch,
    children: children
  });
};